import type { EventMinAge } from '@pulsify/shared/dto'
import { occurrenceResponseToForm } from '~/entities/event/lib/event-occurrences'
import { initialEventPrivacy } from '~/features/events/lib/event-privacy'
import { normalizeCountry } from '~/shared/lib/countries'
import type { Event } from '~/shared/types'

function utcToLocalDatetimeLocal(utcString: string): string {
  const date = new Date(utcString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/** The edit form values for a saved event. */
export function buildEventFormValues(event: Event) {
  return {
    title: event.title,
    description: event.description,
    descriptionLanguage: event.descriptionLanguage,
    descriptionTranslations: event.descriptionTranslations,
    location: {
      name: event.location.name || '',
      street: event.location.street,
      city: event.location.city,
      country: normalizeCountry(event.location.country),
      coordinates: event.location.coordinates,
    },
    locationPending: event.locationPending ?? false,
    addressHidden: event.addressHidden ?? false,
    startTime: utcToLocalDatetimeLocal(event.startTime),
    endTime: utcToLocalDatetimeLocal(event.endTime),
    occurrences: event.occurrences.map(occurrenceResponseToForm),
    price: event.price,
    ticketUrl: event.ticketUrl || '',
    ticketUrlKind: event.ticketUrlKind ?? 'tickets',
    capacity: event.capacity || undefined,
    rsvpEnabled: event.rsvpEnabled,
    liveRoomMode: event.liveRoomMode,
    plusOnesMax: event.plusOnesMax,
    privacy: initialEventPrivacy(event),
    requestEmails: event.requestEmails,
    minAge: event.minAge as EventMinAge | null,
    studentsOnly: event.studentsOnly,
    audienceNote: event.audienceNote ?? '',
    targetAgeMin: event.targetAgeMin ?? undefined,
    targetAgeMax: event.targetAgeMax ?? undefined,
    hostProfileId: event.hostProfileId ?? null,
    tags: {
      mainCategoryId: event.tags.category.id,
      subCategoryTags: event.tags.subCategories.map((sub) => sub.id),
      subSubCategoryTags: event.tags.subSubCategories.map((subSub) => subSub.id),
      amenityIds: event.tags.amenities.map((amenity) => amenity.id),
    },
  }
}

export type EventFormValues = ReturnType<typeof buildEventFormValues>
